import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  makeStyles,
  Typography,
  Divider,
} from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { Auth, API } from "aws-amplify";


const useStyles = makeStyles((theme) => ({
  dialogContent: {
    minWidth: '500px',
    maxHeight: '70vh',
  },
  fieldContainer: {
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: 'bold',
    color: theme.palette.primary.main,
  },
  infoText: {
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
}));

export default function EditFeatureModal({ open, onClose, feature, anmodId, onSave }) {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  
  // Extract properties from the feature
  const originalProperties = feature?.properties?.properties || {};
  const featureConfig = feature?.properties?.config || {};
  const dataId = feature?.properties?.data_id;
  
  // Initialize state with current property values
  const [editedProperties, setEditedProperties] = useState(() => {
    const props = {};
    Object.keys(originalProperties).forEach(key => {
      props[key] = originalProperties[key];
    });
    return props;
  });

  const handlePropertyChange = (key, value) => {
    setEditedProperties(prev => ({
      ...prev,
      [key]: value
    }));
  };

  async function Save(data_id, editedProperties) {
  
      let init = {
        headers: { Authorization: `Bearer ${(await Auth.currentSession()).getIdToken().getJwtToken()}` },
        body: {
            properties: editedProperties
        }
      }
      return await API.put("API", "/Data/" + data_id, init);
    }


  const handleSave = async () => {
    try {
      // Only send properties that have changed
      const changedProperties = {};
      Object.keys(editedProperties).forEach(key => {
        if (editedProperties[key] !== originalProperties[key]) {
          changedProperties[key] = editedProperties[key];
        }
      });

      // Check if there are any changes
      if (Object.keys(changedProperties).length === 0) {
        enqueueSnackbar('No changes to save', { variant: 'info' });
        onClose();
        return;
      }
    
      if (onSave) {
        await Save(dataId, changedProperties);
        enqueueSnackbar('Feature updated successfully', { variant: 'success' });
        onSave(anmodId);
      }

      onClose();
    } catch (error) {
      console.error('Error saving feature:', error);
      enqueueSnackbar('Failed to update feature', { variant: 'error' });
    }
  };

  const getFeatureTitle = () => {
    if (featureConfig?.transform?.elementType) {
      return featureConfig.transform.elementType;
    }
    if (featureConfig?.feature?.featuretype) {
      return featureConfig.feature.featuretype;
    }
    return 'Feature';
  };

  const getFeatureIdentifier = () => {
    const identCol = featureConfig?.feature?.ident_col;
    if (identCol && originalProperties[identCol]) {
      return `${identCol}: ${originalProperties[identCol]}`;
    }
    return dataId ? `ID: ${dataId}` : '';
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Rediger Feature
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        <Typography variant="subtitle2" className={classes.sectionTitle}>
          {getFeatureTitle()}
        </Typography>
        <Typography variant="body2" className={classes.infoText}>
          {getFeatureIdentifier()}
        </Typography>
        
        <Divider className={classes.divider} />

        <Typography variant="subtitle2" className={classes.sectionTitle}>
          Egenskaber
        </Typography>

        {Object.keys(editedProperties).map((key) => {
          const value = editedProperties[key];
          const isString = typeof value === 'string';
          const isNumber = typeof value === 'number';
          const isBoolean = typeof value === 'boolean';
          
          return (
            <div key={key} className={classes.fieldContainer}>
              <TextField
                fullWidth
                label={key}
                value={value === null || value === undefined ? '' : value}
                onChange={(e) => handlePropertyChange(key, e.target.value)}
                variant="outlined"
                size="small"
                multiline={isString && String(value).length > 50}
                minRows={isString && String(value).length > 50 ? 3 : 1}
                helperText={
                  isBoolean ? 'Boolean value' :
                  isNumber ? 'Numeric value' :
                  ''
                }
              />
            </div>
          );
        })}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="default">
          Annuller
        </Button>
        <Button onClick={handleSave} color="primary" variant="contained">
          Gem Ændringer
        </Button>
      </DialogActions>
    </Dialog>
  );
}
